<template>
  <!--
    Contract Scoring — the contract-level half of a tracker spec: the raw
    `scoring_config` JSON EAV on a CPY entity (methodology, rollup, unit,
    max_score, thresholds, min_denominator, member_count).
    POST stars-performance/config/{cpy}/

    `methodology` is the single dispatch key — it selects the scorer, the rollup,
    the display unit and the tracker renderer.

    The OTHER half (each measure's weight / target_rate / tiers, i.e. its
    relationship to this CPY) lives on the Contract Assignments tab. Deliberately
    NOT duplicated here.
  -->
  <div class="h-full overflow-y-auto ms2-scroll">
    <div class="p-4 flex flex-col gap-3">

      <!-- info banner -->
      <div
        class="flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg border text-xs leading-relaxed"
        :style="{ background: ACCENT + '0D', borderColor: ACCENT + '33', color: '#374151' }"
      >
        <font-awesome-icon :icon="['fas', 'circle-info']" class="mt-0.5 flex-shrink-0" :style="{ color: ACCENT }" />
        <span>
          How a contract scores overall — its scoring_config. The methodology selects the
          scorer, the rollup and the tracker's display idiom. Per-measure weights, goals and
          tiers live on Contract Assignments.
        </span>
      </div>

      <!-- Year → Payer → Contract. Each narrows the next; changing an upstream
           choice clears the ones below it rather than leaving a stale selection. -->
      <div class="flex items-center gap-3 px-1">
        <label class="flex items-center gap-1.5">
          <span class="text-[11px] font-medium text-gray-600">Year</span>
          <select
            v-model="selectedYear"
            class="text-xs border border-gray-200 rounded-lg px-2 py-2 bg-white text-gray-700 w-28"
          >
            <option :value="null">All</option>
            <option v-for="y in years" :key="y" :value="y">{{ y === NO_YEAR ? 'No year' : y }}</option>
          </select>
        </label>

        <label class="flex items-center gap-1.5">
          <span class="text-[11px] font-medium text-gray-600">Payer</span>
          <select
            v-model="selectedPayer"
            class="text-xs border border-gray-200 rounded-lg px-2 py-2 bg-white text-gray-700 min-w-[12rem]"
          >
            <option :value="null">All payers</option>
            <option v-for="p in payers" :key="p" :value="p">{{ p }}</option>
          </select>
        </label>

        <label class="flex items-center gap-1.5">
          <span class="text-[11px] font-medium text-gray-600">Contract</span>
          <select
            v-model="selectedCpy"
            class="text-xs border border-gray-200 rounded-lg px-2 py-2 bg-white text-gray-700 min-w-[20rem]"
            @change="loadSpec"
          >
            <option :value="null">Select…</option>
            <option v-for="c in availableContracts" :key="c.cpy_entity_id" :value="c.cpy_entity_id">
              {{ c._label }}{{ c.configured ? '' : '  · not configured' }}
            </option>
          </select>
        </label>

        <span class="text-[11px] text-gray-400 whitespace-nowrap">
          {{ availableContracts.length }} of {{ trackers.length }}
        </span>
        <div class="flex-1"></div>
        <span v-if="saveState" class="text-[11px]" :class="saveState.ok ? 'text-green-600' : 'text-red-600'">
          {{ saveState.text }}
        </span>
      </div>

      <div v-if="loading" class="text-[11px] text-gray-500 px-1 py-6">Loading…</div>
      <div v-else-if="loadError" class="text-[11px] text-red-600 px-1 py-6">{{ loadError }}</div>

      <template v-else-if="selectedCpy">
        <div class="bg-white border border-gray-200 rounded-xl">
          <div class="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2">
            <span class="text-xs font-semibold text-gray-900">Contract Parameters</span>
            <span class="text-[11px] text-gray-400">scoring_config JSON</span>
            <div class="flex-1"></div>
          </div>

          <div class="p-4">
            <textarea
              v-model="cfgText"
              rows="10"
              spellcheck="false"
              class="w-full px-3 py-2 text-xs font-mono border border-gray-200 rounded focus:outline-none focus:border-blue-400"
              :class="jsonError ? 'border-red-300' : ''"
            ></textarea>
            <div class="mt-1 text-[11px]" :class="jsonError ? 'text-red-600' : 'text-gray-400'">
              {{ jsonError || methodologyHint }}
            </div>
          </div>

          <div v-if="spec && spec.validation_errors.length" class="px-4 pb-3">
            <ul class="list-disc pl-4 space-y-0.5">
              <li v-for="(e, i) in spec.validation_errors" :key="i" class="text-[11px] text-amber-700">{{ e }}</li>
            </ul>
          </div>

          <div class="px-4 py-2.5 border-t border-gray-100 flex items-center gap-2">
            <button
              class="inline-flex items-center gap-1.5 text-xs font-semibold text-white rounded-lg px-3 py-1.5 hover:opacity-90 disabled:opacity-40"
              :style="{ background: ACCENT }"
              :disabled="!!jsonError || !cfgDirty || savingCfg"
              @click="saveConfig"
            >{{ savingCfg ? 'Saving…' : 'Save parameters' }}</button>
            <button
              v-if="cfgDirty"
              class="text-[11px] text-gray-500 hover:text-gray-800 underline"
              @click="resetConfig"
            >Discard</button>
          </div>
        </div>

      </template>

      <div v-else class="text-[11px] text-gray-400 px-1 py-10 text-center">
        Select a contract performance year to edit its scoring.
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

const ACCENT = '#059669';
const NO_YEAR = '__none__';

export default {
  name: 'QualityContractScoring',
  data() {
    return {
      ACCENT,
      NO_YEAR,
      trackers: [],
      methodologies: [],
      rollups: [],
      selectedYear: String(new Date().getFullYear()),
      selectedPayer: null,
      selectedCpy: null,
      spec: null,
      cfgText: '',
      cfgOriginal: '',
      loading: false,
      loadError: null,
      savingCfg: false,
      saveState: null,
    };
  },
  computed: {
    // Every field is a real entity name or identifier from the backend:
    // payer_name (Payer entity), contract_name (Contract entity),
    // performance_year (the CPY's CONTRACT_PERFORMANCE_YEAR identifier).
    // Nothing is parsed out of a name string here.
    rows() {
      return this.trackers.map((t) => ({
        ...t,
        _payer: t.payer_name || 'Unassigned payer',
        _label: t.contract_name || t.cpy_entity_name,
      }));
    },
    years() {
      const ys = [...new Set(this.rows.map((r) => r.performance_year).filter(Boolean))]
        .sort().reverse();
      if (this.rows.some((r) => !r.performance_year)) ys.push(NO_YEAR);
      return ys;
    },
    payers() {
      const scoped = this.selectedYear
        ? this.rows.filter((r) => this.matchesYear(r))
        : this.rows;
      return [...new Set(scoped.map((r) => r._payer))].sort();
    },
    availableContracts() {
      return this.rows
        .filter((r) => !this.selectedYear || this.matchesYear(r))
        .filter((r) => !this.selectedPayer || r._payer === this.selectedPayer)
        .sort((a, b) => a._label.localeCompare(b._label));
    },
    parsedCfg() {
      try { return JSON.parse(this.cfgText || '{}'); } catch { return null; }
    },
    jsonError() {
      if (this.parsedCfg === null) return 'Invalid JSON';
      if (this.parsedCfg && typeof this.parsedCfg !== 'object') return 'Must be a JSON object';
      return null;
    },
    activeMethodology() {
      const k = (this.parsedCfg || {}).methodology;
      return this.methodologies.find((m) => m.key === k) || null;
    },
    // The valid keys aren't guessable from a blank textarea — list them, and
    // the chosen methodology's own defaults, right under the field.
    methodologyHint() {
      const keys = 'methodology · rollup · unit · max_score · savings_threshold · ideal_target · min_denominator · member_count';
      const m = this.activeMethodology;
      if (!m) {
        return `${keys}    |    methodologies: ${this.methodologies.map((x) => x.key).join(', ')}`;
      }
      return `${keys}    |    ${m.key}: default rollup "${m.default_rollup}", unit "${m.unit}"`;
    },
    cfgDirty() {
      return this.cfgText.trim() !== this.cfgOriginal;
    },
  },
  watch: {
    // An upstream change can orphan a downstream selection — clear it rather
    // than leave a value that's no longer in its own list.
    selectedYear() {
      if (this.selectedPayer && !this.payers.includes(this.selectedPayer)) this.selectedPayer = null;
      this.clearContractIfMissing();
    },
    selectedPayer() {
      this.clearContractIfMissing();
    },
  },
  mounted() {
    this.loadReference();
  },
  methods: {
    matchesYear(r) {
      return this.selectedYear === NO_YEAR
        ? !r.performance_year
        : r.performance_year === this.selectedYear;
    },
    clearContractIfMissing() {
      if (!this.selectedCpy) return;
      if (!this.availableContracts.some((c) => c.cpy_entity_id === this.selectedCpy)) {
        this.selectedCpy = null;
        this.spec = null;
        this.saveState = null;
      }
    },

    async loadReference() {
      try {
        const [tr, me] = await Promise.all([
          axios.get('/api/stars-performance/config-contracts/'),
          axios.get('/api/stars-performance/methodologies/'),
        ]);
        this.trackers = tr.data.contracts || [];
        // Default to the current year; fall back to the newest year present.
        if (!this.years.includes(this.selectedYear)) {
          this.selectedYear = this.years[0] || null;
        }
        this.methodologies = me.data.methodologies || [];
        this.rollups = me.data.rollups || [];
      } catch (e) {
        this.loadError = e.response?.data?.error || 'Failed to load contracts.';
      }
    },

    async loadSpec() {
      this.spec = null;
      this.saveState = null;
      if (!this.selectedCpy) return;
      this.loading = true;
      this.loadError = null;
      try {
        // Postgres-only read of the stored blob. Deliberately NOT tracker/{cpy}/,
        // which also makes four Snowflake round-trips and scores every measure.
        const { data } = await axios.get(`/api/stars-performance/config/${this.selectedCpy}/`);
        this.spec = { config: data.config || {}, validation_errors: [] };
        this.hydrateCfg();
      } catch (e) {
        this.loadError = e.response?.data?.error || 'Failed to load contract.';
      } finally {
        this.loading = false;
      }
    },

    hydrateCfg() {
      this.cfgText = JSON.stringify((this.spec || {}).config || {}, null, 2);
      this.cfgOriginal = this.cfgText.trim();
    },

    resetConfig() { this.hydrateCfg(); this.saveState = null; },

    async saveConfig() {
      this.savingCfg = true;
      this.saveState = null;
      try {
        const { data } = await axios.post(
          `/api/stars-performance/config/${this.selectedCpy}/`,
          { config: this.parsedCfg },
        );
        this.spec = { ...(this.spec || {}), validation_errors: data.validation_errors || [] };
        this.cfgOriginal = this.cfgText.trim();
        this.saveState = data.valid
          ? { ok: true, text: 'Saved' }
          : { ok: false, text: `Saved — ${data.validation_errors.length} to fix` };
      } catch (e) {
        this.saveState = { ok: false, text: e.response?.data?.error || 'Save failed' };
      } finally {
        this.savingCfg = false;
      }
    },

  },
};
</script>
